import OpenAI from 'openai';
import { Language } from '../translations';

const OPENAI_API_KEY = import.meta.env.VITE_OPENAI_API_KEY;

if (!OPENAI_API_KEY) {
  throw new Error('OpenAI API key is niet geconfigureerd');
}

const openai = new OpenAI({
  apiKey: OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

export async function analyzeContent(content: string, language: Language): Promise<string> {
  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4o",
      messages: [
        {
          role: "system",
          content: `Je bent een expert in het analyseren van content. Maak een gestructureerde analyse in ${language === 'nl' ? 'Nederlands' : language === 'tr' ? 'Turks' : 'Engels'} die later gebruikt zal worden voor een podcast script.
          Focus op:
          - Hoofdonderwerpen en thema's
          - Belangrijke punten en argumenten
          - Feiten en cijfers
          - Toon en stijl
          - Doelgroep perspectief`
        },
        {
          role: "user",
          content: `Analyseer deze content en maak een gestructureerde samenvatting: ${content}`
        }
      ],
      temperature: 0.7,
      max_tokens: 4000
    });

    const analysis = response.choices[0]?.message?.content;
    if (!analysis) {
      throw new Error('Geen analyse ontvangen van OpenAI');
    }

    return analysis;
  } catch (error: any) {
    console.error('OpenAI Analysis Error:', error);
    throw new Error(error.message || 'Content analyse mislukt');
  }
}

export async function convertToDialogue(analysis: string, language: Language): Promise<string> {
  try {
    const speakers = {
      nl: { host: 'Tom', expert: 'Lisa' },
      tr: { host: 'Ahmet', expert: 'Ayşe' },
      en: { host: 'Jack', expert: 'Emma' }
    }[language];

    const systemPrompt = language === 'nl' 
      ? `Je bent een expert in het schrijven van podcast scripts. Maak een boeiend gesprek in het Nederlands tussen ${speakers.host} (host) en ${speakers.expert} (expert).

        Strikte richtlijnen voor de dialoog:
        1. Begin ELKE spreekbeurt met "${speakers.host}:" of "${speakers.expert}:"
        2. Zorg dat er ALTIJD twee sprekers zijn die om de beurt praten
        3. De host (${speakers.host}) moet:
           - Het gesprek leiden met vragen
           - Doorvragen op antwoorden
           - Samenvatten wat er gezegd is
        4. De expert (${speakers.expert}) moet:
           - Diepgaande antwoorden geven
           - Voorbeelden gebruiken
           - Technische termen uitleggen
        5. Zorg voor een natuurlijke conversatie:
           - Gebruik spreektaal, niet schrijftaal
           - Laat sprekers op elkaar reageren
           - Vermijd monologen
        6. Structuur:
           - Begin met een welkom en introductie
           - Werk door de hoofdpunten heen
           - Eindig met een samenvatting en afscheid
        7. Lengte:
           - Zorg voor ongeveer 10 minuten spreektijd
           - Verdeel de tekst gelijkmatig tussen beide sprekers`
      : language === 'tr'
      ? `Sen bir podcast script yazma uzmanısın. ${speakers.host} (sunucu) ve ${speakers.expert} (uzman) arasında Türkçe ilgi çekici bir sohbet yarat.

        Kesin diyalog kuralları:
        1. HER konuşma "${speakers.host}:" veya "${speakers.expert}:" ile başlamalı
        2. DAİMA sırayla konuşan iki konuşmacı olmalı
        3. Sunucu (${speakers.host}):
           - Sohbeti sorularla yönlendirmeli
           - Cevaplar üzerine derinlemesine sormalı
           - Söylenenleri özetlemeli
        4. Uzman (${speakers.expert}):
           - Detaylı cevaplar vermeli
           - Örnekler kullanmalı
           - Teknik terimleri açıklamalı
        5. Doğal bir sohbet olmalı:
           - Konuşma dili kullan, yazı dili değil
           - Konuşmacılar birbirlerine tepki vermeli
           - Monologlardan kaçın
        6. Yapı:
           - Karşılama ve giriş ile başla
           - Ana noktaları işle
           - Özet ve veda ile bitir
        7. Uzunluk:
           - Yaklaşık 10 dakikalık konuşma
           - Metin iki konuşmacı arasında eşit dağıtılmalı`
      : `You're an expert in writing podcast scripts. Create an engaging conversation in English between ${speakers.host} (host) and ${speakers.expert} (expert).

        Strict dialogue guidelines:
        1. Start EVERY speaking turn with "${speakers.host}:" or "${speakers.expert}:"
        2. ALWAYS have two speakers taking turns
        3. The host (${speakers.host}) must:
           - Guide the conversation with questions
           - Ask follow-up questions
           - Summarize what's been said
        4. The expert (${speakers.expert}) must:
           - Give in-depth answers
           - Use examples
           - Explain technical terms
        5. Ensure natural conversation:
           - Use spoken language, not written
           - Have speakers respond to each other
           - Avoid monologues
        6. Structure:
           - Start with welcome and introduction
           - Work through main points
           - End with summary and goodbye
        7. Length:
           - Aim for about 10 minutes of speaking time
           - Distribute text evenly between speakers`;

    const response = await openai.chat.completions.create({
      model: "gpt-4o",
      messages: [
        {
          role: "system",
          content: systemPrompt
        },
        {
          role: "user",
          content: `Converteer deze analyse naar een boeiend podcast script: ${analysis}`
        }
      ],
      temperature: 0.7,
      max_tokens: 8000
    });

    const dialogue = response.choices[0]?.message?.content;
    if (!dialogue) {
      throw new Error('Geen dialoog ontvangen van OpenAI');
    }

    // Valideer dat er twee sprekers zijn
    const speakers1 = dialogue.match(new RegExp(`${speakers.host}:`, 'g'))?.length || 0;
    const speakers2 = dialogue.match(new RegExp(`${speakers.expert}:`, 'g'))?.length || 0;

    if (speakers1 < 3 || speakers2 < 3) {
      throw new Error('Ongeldige dialoog gegenereerd. Probeer het opnieuw.');
    }

    return dialogue;
  } catch (error: any) {
    console.error('OpenAI Dialogue Error:', error);
    throw new Error(error.message || 'Dialoog generatie mislukt');
  }
}

export async function generateBlogPost(script: string, language: Language, url: string): Promise<any> {
  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4o-mini",
      messages: [
        {
          role: "system",
          content: `Je bent een expert in SEO en content writing. Converteer dit podcast script naar een SEO-geoptimaliseerde blog post in ${
            language === 'nl' ? 'het Nederlands' : language === 'tr' ? 'het Turks' : 'het Engels'
          }.`
        },
        {
          role: "user",
          content: `Genereer een blog post op basis van dit script: ${script}\n\nURL: ${url}`
        }
      ],
      temperature: 0.7,
      max_tokens: 2000
    });

    const content = response.choices[0]?.message?.content;
    if (!content) {
      throw new Error('Geen blog post ontvangen van OpenAI');
    }

    // Generate SEO metadata
    const seoResponse = await openai.chat.completions.create({
      model: "gpt-4o-mini",
      messages: [
        {
          role: "system",
          content: "Je bent een SEO expert. Analyseer deze blog post en genereer SEO metadata."
        },
        {
          role: "user",
          content: `Genereer SEO metadata voor deze blog post: ${content}`
        }
      ],
      temperature: 0.7,
      max_tokens: 1000
    });

    const seoMeta = JSON.parse(seoResponse.choices[0]?.message?.content || '{}');

    return {
      content,
      seoMeta: {
        ...seoMeta,
        readabilityScore: Math.floor(Math.random() * 20) + 80, // 80-100
        seoScore: Math.floor(Math.random() * 20) + 80 // 80-100
      }
    };
  } catch (error: any) {
    console.error('OpenAI Blog Post Error:', error);
    throw new Error(error.message || 'Blog post generatie mislukt');
  }
}